import { createContext, useContext, useEffect, useMemo, useState } from "react";
import apiClient, { getApiErrorMessage, isNetworkError } from "../api/client";
import { logWarn } from "../lib/logger";
import { isPublicPath } from "../lib/publicPaths";

const AuthContext = createContext(null);

// Token expiry check — decode JWT payload to check exp claim
function isTokenExpired(token) {
  if (!token) return true;
  try {
    // URL-safe base64 decoding (handles - and _ chars in JWT)
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    return payload.exp ? payload.exp * 1000 < Date.now() : false;
  } catch {
    return true;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Note: localStorage is used for auth tokens to support offline/PWA scenarios.
  // httpOnly cookies would require backend changes and break offline round restore.
  const saveSession = (token, userData) => {
    localStorage.setItem("ggr_auth_token", token);
    localStorage.setItem("ggr_auth_user", JSON.stringify(userData));
    setUser(userData);
  };

  const clearSession = () => {
    localStorage.removeItem("ggr_auth_token");
    localStorage.removeItem("ggr_auth_user");
    setUser(null);
  };

  const syncCurrentUser = async () => {
    try {
      const token = localStorage.getItem("ggr_auth_token");
      if (!token) {
        setIsLoading(false);
        return;
      }
      // Check token expiry locally first — avoid unnecessary API call
      if (isTokenExpired(token)) {
        clearSession();
        setIsLoading(false);
        return;
      }
      const response = await apiClient.get("/auth/me");
      const latestUser = response.data;
      localStorage.setItem("ggr_auth_user", JSON.stringify(latestUser));
      setUser(latestUser);
    } catch (err) {
      // CRITICAL: Only clear session on real auth failure (401/403)
      // NOT on network errors (timeout, offline, etc.)
      if (isNetworkError(err)) {
        // Network issue — keep cached user, don't log out
        const cachedUser = localStorage.getItem("ggr_auth_user");
        if (cachedUser) {
          try { setUser(JSON.parse(cachedUser)); } catch (e) { logWarn("Cached user parse error:", e); }
        }
      } else {
        // Real auth failure (401, expired token, etc.)
        clearSession();
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Skip auth sync on public pages — prevents 401 redirect race condition
    const onPublicPage = isPublicPath(window.location.pathname);

    // Immediately restore cached user for instant app access
    const cachedUser = localStorage.getItem("ggr_auth_user");
    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
        setIsLoading(false);
      } catch {
        localStorage.removeItem("ggr_auth_user");
      }
    }

    // Only sync with server if NOT on a public page
    if (!onPublicPage) {
      syncCurrentUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  const register = async (payload) => {
    const response = await apiClient.post("/auth/register", payload);
    return response.data;
  };

  const login = async (payload) => {
    const response = await apiClient.post("/auth/login", payload);
    saveSession(response.data.access_token, response.data.user);
    return response.data.user;
  };

  const logout = () => {
    clearSession();
  };

  const updateLocalUser = (partialUser) => {
    const merged = { ...user, ...partialUser };
    setUser(merged);
    localStorage.setItem("ggr_auth_user", JSON.stringify(merged));
  };

  const value = useMemo(
    () => ({
      user,
      isLoading,
      register,
      login,
      logout,
      updateLocalUser,
      getApiErrorMessage,
    }),
    [user, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
